import { keyBy, map } from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { useOnMount } from '../../../helpers/hooks/useOnMount';
import { ErrorBox } from '../../../shared/ErrorBox';
import { SearchSelectOptions } from '../../../shared/form/inputs/SearchSelect';
import { getNullSelectOption, SelectOptions } from '../../../shared/form/inputs/Select';
import { PageLoadingIndicator } from '../../../shared/LoadingIndicator';
import { Project } from '../../projects/project-section/ProjectHeader';
import { TechnologyTypeCode } from '../technologyTypeCode';
import { getHeatPumpUnitSelectOptionText } from './heat-pump/obsoleteHeatPumpUnitCodes';

type Props = {
  children: (props: DataForQuoteOrDesignForm) => ReactNode;
  project: Project;
};

export const GetDataForQuoteOrDesignForm = ({ children, project }: Props) => {
  const { response, error, inProgress, sendRequest } = useApiRequest<GetDataForQuoteOrDesignFormResponse>({
    httpMethod: 'get',
    endpointUrl: `projects/${project.projectId}/data-for-quote-or-design-form`,
  });

  const [childrenProps, setChildrenProps] = useState<DataForQuoteOrDesignForm | null>(null);

  useOnMount(() => {
    void sendRequest();
  });

  useEffect(() => {
    if (response != null) {
      setChildrenProps(mapGetDataForQuoteOrDesignFormResponseToOptions(response));
    }
  }, [response]);

  if (inProgress) {
    return <PageLoadingIndicator />;
  }

  if (error != null) {
    return <ErrorBox error={error} />;
  }

  if (childrenProps != null) {
    return <>{children(childrenProps)}</>;
  }

  return null;
};

export type FloorConstructionResponse = {
  partId: number;
  displayName: string;
  isHighOutput: boolean;
  isElectricUnderfloorHeating: boolean;
  pipeSpacingInMillimetres: number | null;
  pipeDiameterInMillimetres: number | null;
  maxPipeLengthPerPortInMetresForNewBuild: number | null;
  maxPipeLengthPerPortInMetresForRetrofit: number | null;
  maxNumberOfPipesPerPort: number;
  defaultDeckingThermalResistancePerMillimetre: number | null;
  defaultDeckingThicknessInMillimetres: number | null;
  mainFloorOutputFormulaType: string | null;
  mainFloorOutputFormulaCoefficientA: number | null;
  mainFloorOutputFormulaCoefficientB: number | null;
  mainFloorOutputFormulaCoefficientC: number | null;
  mainFloorOutputFormulaCoefficientD: number | null;
  mainFloorOutputFormulaCoefficientE: number | null;
  mainFloorOutputFormulaCoefficientF: number | null;
  mainFloorOutputFormulaCoefficientG: number | null;
  peripheralFloorOutputFormulaType: string | null;
  peripheralFloorOutputFormulaCoefficientA: number | null;
  peripheralFloorOutputFormulaCoefficientB: number | null;
  peripheralFloorOutputFormulaCoefficientC: number | null;
  peripheralFloorOutputFormulaCoefficientD: number | null;
  peripheralFloorOutputFormulaCoefficientE: number | null;
  peripheralFloorOutputFormulaCoefficientF: number | null;
  peripheralFloorOutputFormulaCoefficientG: number | null;
  screedConductivity: number | null;
  requiresPeripheralArea: boolean;
  requiresTurnPanelQuantity: boolean;
  requiresPlateArea: boolean;
  requiresStraightPanelQuantity: boolean;
};

export type PumpResponse = {
  partId: number;
  displayName: string;
  maxAreaInMetresSquared: number;
  includesBlendingValve: boolean;
  maxPressureInKilopascals: number;
  pressureLimitInKilopascals: number;
  firstOrder: number;
  secondOrder: number;
};

export type PipeResponse = {
  code: string;
  displayName: string;
  outsideDiameterInMillimetres: number;
  wallThicknessInMillimetres: number;
  internalDiameterInMillimetres: number;
  internalCrossSectionalAreaInMetresSquared: number;
  waterContentInLitresPerMetre: number;
  isUnderfloorHeating: boolean;
};

export type HeatSourceResponse = {
  displayName: string;
  isHeatPump: boolean;
  maxPressureInKilopascals: number | null;
  pressureLimitInKilopascals: number | null;
  firstOrder: number | null;
  secondOrder: number | null;
};

export type SkirtingHeatingResponse = {
  partId: number;
  displayName: string;
  percentageHeated: number | null;
  pipeSpacingInMillimetres: number | null;
  maxPipeLengthPerPortInMetresForNewBuild: number;
  maxPipeLengthPerPortInMetresForRetrofit: number;
  maxNumberOfPipesPerPort: number;
  requiresPeripheralArea: boolean;
  requiresTurnPanelQuantity: boolean;
  requiresPlateArea: boolean;
  requiresStraightPanelQuantity: boolean;
};

export type HeatPumpUnitOutput = {
  flowTemperatureInDegreesCelsius: number;
  outsideTemperatureInDegreesCelsius: number;
  outputInKilowatts: number;
};

export type HeatPumpUnitResponse = {
  partId: number;
  displayName: string;
  isAirSourceHeatPump: boolean;
  suitableForCoastalLocation: boolean;
  outputsAtGivenTemperatures: Array<HeatPumpUnitOutput>;
};

export type MicrogenerationCertificationSchemeLocationResponse = {
  name: string;
  bivalentPointInDegreesCelsius: number;
  heatingDegreeDays: number;
};

export type SupportOptionResponse = {
  partId: number;
  displayName: string;
};

export type SupportOptionMetadataResponse = {
  minimumUnitCountSuitableFor: number;
  maximumUnitCountSuitableFor: number | null;
  suitableForSolar: boolean;
};

export type HeatPumpDockingCodeResponse = {
  code: string;
  displayName: string;
  requiredHeatPumpUnitCount: number | null;
};

export type FloorCoveringResponse = {
  name: string;
  defaultThermalResistancePerMillimetre: number | null;
  defaultThicknessInMillimetres: number | null;
  defaultUnderlayThermalResistancePerMillimetre: number | null;
  defaultUnderlayThicknessInMillimetres: number | null;
  temperatureLimitInDegreesCelsius: number | null;
  isSensitive: boolean;
  isArchived: boolean;
  orderPosition: number;
};

export type SalesOrderResponse = {
  salesOrderId: number;
  technologyTypeCode: TechnologyTypeCode;
  orderNumber: string;
  uniqueReference: string;
  financeStatus: string;
};

const mapSalesOrderResponseToDisplayText = (salesOrderResponse: SalesOrderResponse) =>
  `${salesOrderResponse.uniqueReference} - ${salesOrderResponse.financeStatus} (${salesOrderResponse.orderNumber})`;

type ExtraPartResponse = {
  partId: number;
  name: string;
  description: string;
  isInactive: boolean;
};

export type GetDataForQuoteOrDesignFormResponse = {
  floorConstructions: Array<FloorConstructionResponse>;
  unitPumps: Array<PumpResponse>;
  manifoldPumps: Array<PumpResponse>;
  pipes: Array<PipeResponse>;
  heatSources: Array<HeatSourceResponse>;
  skirtingHeatingParts: Array<SkirtingHeatingResponse>;
  heatPumpUnits: Array<HeatPumpUnitResponse>;
  microgenerationCertificationSchemeLocations: Array<MicrogenerationCertificationSchemeLocationResponse>;
  newBuildSupportOptions: Array<SupportOptionResponse>;
  newBuildSupportOptionsMetadata: { [supportOptionPartCode: string]: SupportOptionMetadataResponse };
  retrofitSupportOptions: Array<SupportOptionResponse>;
  retrofitSupportOptionsMetadata: { [supportOptionPartCode: string]: SupportOptionMetadataResponse };
  airSourceHeatPumpDockingCodes: Array<HeatPumpDockingCodeResponse>;
  groundSourceHeatPumpDockingCodes: Array<HeatPumpDockingCodeResponse>;
  heatPumpPartCodesByDockingTypeCode: { [dockingTypeCode: string]: Array<string> };
  floorCoverings: Array<FloorCoveringResponse>;
  salesOrders: Array<SalesOrderResponse>;
  extraParts: Array<ExtraPartResponse>;
};

export const floorOutputFormulaTypeEn1264 = 'EN1264';

export type RoomFloorConstructionFormModel = {
  partId: number;
  displayName: string;
  isHighOutput: boolean;
  isSkirtingHeating: boolean;
  percentageHeated: number | null;
  isElectricUnderfloorHeating: boolean;
  pipeSpacingInMillimetres: number | null;
  pipeDiameterInMillimetres: number | null;
  maxPipeLengthPerPortInMetresForNewBuild: number | null;
  maxPipeLengthPerPortInMetresForRetrofit: number | null;
  maxNumberOfPipesPerPort: number;
  defaultDeckingThermalResistancePerMillimetre: number | null;
  defaultDeckingThicknessInMillimetres: number | null;
  mainFloorOutputFormulaType: string | null;
  mainFloorOutputFormulaCoefficientA: number | null;
  mainFloorOutputFormulaCoefficientB: number | null;
  mainFloorOutputFormulaCoefficientC: number | null;
  mainFloorOutputFormulaCoefficientD: number | null;
  mainFloorOutputFormulaCoefficientE: number | null;
  mainFloorOutputFormulaCoefficientF: number | null;
  mainFloorOutputFormulaCoefficientG: number | null;
  peripheralFloorOutputFormulaType: string | null;
  peripheralFloorOutputFormulaCoefficientA: number | null;
  peripheralFloorOutputFormulaCoefficientB: number | null;
  peripheralFloorOutputFormulaCoefficientC: number | null;
  peripheralFloorOutputFormulaCoefficientD: number | null;
  peripheralFloorOutputFormulaCoefficientE: number | null;
  peripheralFloorOutputFormulaCoefficientF: number | null;
  peripheralFloorOutputFormulaCoefficientG: number | null;
  screedConductivity: number | null;
  requiresPeripheralArea: boolean;
  requiresTurnPanelQuantity: boolean;
  requiresPlateArea: boolean;
  requiresStraightPanelQuantity: boolean;
};

export type ManifoldFloorConstructionFormModel = {
  partId: number;
  displayName: string;
  isHighOutput: boolean;
  isElectricUnderfloorHeating: boolean;
};

export type PumpFormModel = {
  partId: number;
  displayName: string;
  maxAreaInMetresSquared: number;
  includesBlendingValve: boolean;
  maxPressureInKilopascals: number;
  pressureLimitInKilopascals: number;
  firstOrder: number;
  secondOrder: number;
};

export type PipeFormModel = {
  code: string;
  displayName: string;
  outsideDiameterInMillimetres: number;
  wallThicknessInMillimetres: number;
  internalDiameterInMillimetres: number;
  internalCrossSectionalAreaInMetresSquared: number;
  waterContentInLitresPerMetre: number;
  isUnderfloorHeating: boolean;
};

export type HeatSourceFormModel = {
  displayName: string;
  isHeatPump: boolean;
  pump: {
    maxPressureInKilopascals: number;
    pressureLimitInKilopascals: number;
    firstOrder: number;
    secondOrder: number;
  } | null;
};

export type HeatPumpUnitFormModel = {
  partId: number;
  displayName: string;
  isAirSourceHeatPump: boolean;
  suitableForCoastalLocation: boolean;
  outputsAtGivenTemperatures: Array<HeatPumpUnitOutput>;
};

export type MicrogenerationCertificationSchemeLocationFormModel = {
  name: string;
  bivalentPointInDegreesCelsius: number;
  heatingDegreeDays: number;
};

export type SupportOptionMetadata = {
  minimumUnitCountSuitableFor: number;
  maximumUnitCountSuitableFor: number | null;
  suitableForSolar: boolean;
};

export type FloorCovering = {
  name: string;
  defaultThermalResistancePerMillimetre: number | null;
  defaultThicknessInMillimetres: number | null;
  defaultUnderlayThermalResistancePerMillimetre: number | null;
  defaultUnderlayThicknessInMillimetres: number | null;
  temperatureLimitInDegreesCelsius: number | null;
  isSensitive: boolean;
  isArchived: boolean;
};

type ExtraPart = ExtraPartResponse;

export type DataForQuoteOrDesignForm = {
  roomFloorConstructionOptions: SelectOptions<RoomFloorConstructionFormModel>;
  manifoldFloorConstructionOptions: SelectOptions<ManifoldFloorConstructionFormModel>;
  unitPumpOptions: SelectOptions<PumpFormModel | null>;
  manifoldPumpOptions: SelectOptions<PumpFormModel | null>;
  pipeOptions: SelectOptions<PipeFormModel | null>;
  heatSourceOptions: SelectOptions<HeatSourceFormModel | null>;
  heatPumpUnitOptions: SelectOptions<HeatPumpUnitFormModel | null>;
  microgenerationCertificationSchemeLocationOptions: SelectOptions<MicrogenerationCertificationSchemeLocationFormModel | null>;
  newBuildSupportOptions: SelectOptions<number>;
  newBuildSupportOptionsMetadata: { [supportOptionPartCode: string]: SupportOptionMetadata };
  retrofitSupportOptions: SelectOptions<number>;
  retrofitSupportOptionsMetadata: { [supportOptionPartCode: string]: SupportOptionMetadata };
  airSourceDockingCodeOptions: SelectOptions<string | null>;
  groundSourceDockingCodeOptions: SelectOptions<string | null>;
  heatPumpPartCodesByDockingTypeCode: { [dockingTypeCode: string]: Array<string> };
  requiredHeatPumpUnitCountByDockingCode: { [dockingCode: string]: number | null };
  floorCoveringsByName: { [name: string]: FloorCovering };
  underfloorHeatingSalesOrderOptions: SelectOptions<number | null>;
  heatPumpSalesOrderOptions: SelectOptions<number | null>;
  solarSalesOrderOptions: SelectOptions<number | null>;
  extraPartsById: { [partId: number]: ExtraPart };
  extraPartOptions: SearchSelectOptions<number | null>;
};

export const mapGetDataForQuoteOrDesignFormResponseToOptions = (
  response: GetDataForQuoteOrDesignFormResponse,
): DataForQuoteOrDesignForm => {
  const floorConstructionOptions = map(response.floorConstructions, construction => ({
    value: { ...construction, isSkirtingHeating: false, percentageHeated: null },
    text: construction.displayName,
  }));

  const skirtingHeatingOptions: SelectOptions<RoomFloorConstructionFormModel> = map(
    response.skirtingHeatingParts,
    part => ({
      value: {
        ...part,
        isHighOutput: false,
        isSkirtingHeating: true,
        isElectricUnderfloorHeating: false,
        defaultDeckingThermalResistancePerMillimetre: null,
        defaultDeckingThicknessInMillimetres: null,
        pipeDiameterInMillimetres: null,
        screedConductivity: null,
        mainFloorOutputFormulaType: null,
        mainFloorOutputFormulaCoefficientA: null,
        mainFloorOutputFormulaCoefficientB: null,
        mainFloorOutputFormulaCoefficientC: null,
        mainFloorOutputFormulaCoefficientD: null,
        mainFloorOutputFormulaCoefficientE: null,
        mainFloorOutputFormulaCoefficientF: null,
        mainFloorOutputFormulaCoefficientG: null,
        peripheralFloorOutputFormulaType: null,
        peripheralFloorOutputFormulaCoefficientA: null,
        peripheralFloorOutputFormulaCoefficientB: null,
        peripheralFloorOutputFormulaCoefficientC: null,
        peripheralFloorOutputFormulaCoefficientD: null,
        peripheralFloorOutputFormulaCoefficientE: null,
        peripheralFloorOutputFormulaCoefficientF: null,
        peripheralFloorOutputFormulaCoefficientG: null,
      },
      text: part.displayName,
    }),
  );

  const unitPumpOptions = [
    getNullSelectOption<PumpFormModel>(),
    ...map(response.unitPumps, pump => ({ value: pump, text: pump.displayName })),
  ];

  const manifoldPumpOptions = [
    getNullSelectOption<PumpFormModel>(),
    ...map(response.manifoldPumps, pump => ({ value: pump, text: pump.displayName })),
  ];

  const pipeOptions = [
    getNullSelectOption<PipeFormModel>(),
    ...map(response.pipes, pipe => ({ value: pipe, text: pipe.displayName })),
  ];

  const heatSourceOptions = [
    getNullSelectOption<HeatSourceFormModel>(),
    ...map(response.heatSources, heatSource => {
      const pump = heatSource.isHeatPump
        ? null
        : {
            maxPressureInKilopascals: heatSource.maxPressureInKilopascals ?? 0,
            pressureLimitInKilopascals: heatSource.pressureLimitInKilopascals ?? 0,
            firstOrder: heatSource.firstOrder ?? 0,
            secondOrder: heatSource.secondOrder ?? 0,
          };

      return {
        value: { displayName: heatSource.displayName, isHeatPump: heatSource.isHeatPump, pump },
        text: heatSource.displayName,
      };
    }),
  ];

  const heatPumpUnitSizeRegex = /(?<=\/)(\d+)(?!\d)/;

  const heatPumpUnitOptions = [
    getNullSelectOption<HeatPumpUnitFormModel>(),
    ...response.heatPumpUnits
      .sort((a, b) => {
        const aSize = a.displayName.match(heatPumpUnitSizeRegex);
        const bSize = b.displayName.match(heatPumpUnitSizeRegex);
        if (aSize && bSize) {
          return Number(aSize[0]) - Number(bSize[0]);
        }
        if (aSize) return 1;
        if (bSize) return -1;
        return 0;
      })
      .map(heatPump => ({ value: heatPump, text: getHeatPumpUnitSelectOptionText(heatPump.displayName) })),
  ];

  const microgenerationCertificationSchemeLocationOptions = [
    getNullSelectOption<MicrogenerationCertificationSchemeLocationFormModel>(),
    ...response.microgenerationCertificationSchemeLocations.map(location => ({ value: location, text: location.name })),
  ];

  const newBuildSupportOptions = response.newBuildSupportOptions.map(supportOption => ({
    value: supportOption.partId,
    text: supportOption.displayName,
  }));

  const newBuildSupportOptionsMetadata = response.newBuildSupportOptionsMetadata;

  const retrofitSupportOptions = response.retrofitSupportOptions.map(supportOption => ({
    value: supportOption.partId,
    text: supportOption.displayName,
  }));

  const retrofitSupportOptionsMetadata = response.retrofitSupportOptionsMetadata;

  const airSourceDockingCodeOptions = [
    getNullSelectOption<string>(),
    ...response.airSourceHeatPumpDockingCodes.map(dockingCode => ({
      value: dockingCode.code,
      text: dockingCode.displayName,
    })),
  ];

  const groundSourceDockingCodeOptions = [
    getNullSelectOption<string>(),
    ...response.groundSourceHeatPumpDockingCodes.map(dockingCode => ({
      value: dockingCode.code,
      text: dockingCode.displayName,
    })),
  ];

  const requiredHeatPumpUnitCountByDockingCode = [
    ...response.airSourceHeatPumpDockingCodes,
    ...response.groundSourceHeatPumpDockingCodes,
  ].reduce<{ [dockingCode: string]: number | null }>((countsByCode, dockingCode) => {
    if (!(dockingCode.code in countsByCode)) {
      countsByCode[dockingCode.code] = dockingCode.requiredHeatPumpUnitCount;
    }
    return countsByCode;
  }, {});

  const floorCoveringsByName = keyBy(response.floorCoverings, floorCovering => floorCovering.name);

  const underfloorHeatingSalesOrderOptions = response.salesOrders
    .filter(so => so.technologyTypeCode === 'UnderfloorHeating')
    .map(so => ({ value: so.salesOrderId, text: mapSalesOrderResponseToDisplayText(so) }));

  const heatPumpSalesOrderOptions = response.salesOrders
    .filter(so => so.technologyTypeCode === 'HeatPump')
    .map(so => ({ value: so.salesOrderId, text: mapSalesOrderResponseToDisplayText(so) }));

  const solarSalesOrderOptions = response.salesOrders
    .filter(so => so.technologyTypeCode === 'Solar')
    .map(so => ({ value: so.salesOrderId, text: mapSalesOrderResponseToDisplayText(so) }));

  const extraPartsById = keyBy(response.extraParts, extraPart => extraPart.partId);

  const extraPartOptions = response.extraParts
    .filter(extraPart => !extraPart.isInactive)
    .map(extraPart => ({ value: extraPart.partId, text: extraPart.name }));

  return {
    roomFloorConstructionOptions: [...floorConstructionOptions, ...skirtingHeatingOptions],
    manifoldFloorConstructionOptions: floorConstructionOptions,
    unitPumpOptions,
    manifoldPumpOptions,
    pipeOptions,
    heatSourceOptions,
    heatPumpUnitOptions,
    microgenerationCertificationSchemeLocationOptions,
    newBuildSupportOptions,
    newBuildSupportOptionsMetadata,
    retrofitSupportOptions,
    retrofitSupportOptionsMetadata,
    airSourceDockingCodeOptions,
    groundSourceDockingCodeOptions,
    heatPumpPartCodesByDockingTypeCode: response.heatPumpPartCodesByDockingTypeCode,
    requiredHeatPumpUnitCountByDockingCode,
    floorCoveringsByName,
    underfloorHeatingSalesOrderOptions: [getNullSelectOption<number>(), ...underfloorHeatingSalesOrderOptions],
    heatPumpSalesOrderOptions: [getNullSelectOption<number>(), ...heatPumpSalesOrderOptions],
    solarSalesOrderOptions: [getNullSelectOption<number>(), ...solarSalesOrderOptions],
    extraPartsById,
    extraPartOptions,
  };
};

export const getFloorConstructionOrNull = (
  floorConstructionPartId: number | null,
  parts: DataForQuoteOrDesignForm,
): RoomFloorConstructionFormModel | null => {
  if (floorConstructionPartId == null) {
    return null;
  }

  const matchingOption = parts.roomFloorConstructionOptions.filter(
    option => option.value.partId === floorConstructionPartId,
  )[0];

  return matchingOption == null ? null : matchingOption.value;
};
